import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button, cn } from '@eombor/ui';
import { t } from '../i18n/index.js';
import { useMediaQuery } from './useMediaQuery.js';

export interface PageHeaderProps {
  /** Sahifa nomi — 20px/600. */
  title: ReactNode;
  /** Bir jumlalik izoh — 20px sarlavha nima qilishini aytadi. */
  description?: ReactNode;
  /**
   * "Orqaga" havolasi — sarlavhaning USTIDA turadi.
   *
   * Nega ustida: u sahifaning harakati emas, undan CHIQISH yo'li. O'ng
   * tomondagi tugmalar qatoriga qo'yilsa, u yerdagi "yangi talabnoma" bilan
   * bir darajaga tushib qolardi.
   */
  back?: ReactNode;
  /**
   * Ro'yxatni QISQARTIRADIGAN boshqaruvlar (qidiruv, tanlagich, bayroqcha).
   * Tor ekranda ular "Filtrlar" tugmasi ostidagi panelga yig'iladi.
   */
  filters?: ReactNode;
  /** Filtrlar guruhining nomi — ekran o'qigich uchun. */
  filtersLabel?: string;
  /** Sahifa HARAKATLARI ("Yangi talabnoma", "Yangilash") — doim ko'rinadi. */
  actions?: ReactNode;
  className?: string;
}

/** Shu kenglikdan boshlab filtrlar sarlavha bilan bitta qatorda turadi. */
const WIDE = '(min-width: 768px)';

/**
 * SAHIFA SARLAVHASI.
 *
 * O'n ikki sahifada bir xil o'n qator JSX takrorlanardi va ular allaqachon
 * bir-biridan uzoqlashib ketgandi: ikki sahifada tugma sarlavhaning YUQORI
 * chetiga, qolganida pastki chetiga tekislangan; boshqaruvlar orasidagi
 * bo'shliq 8px, tanlagich 44px, tugma esa 40px bo'lgani uchun ikki sahifada
 * tugmaga qo'lda `h-[var(--size-control)]` yopishtirilgan edi.
 *
 * Endi tekislash bitta joyda: `items-end` — chunki sarlavha bilan
 * boshqaruvni bog'laydigan chiziq ularning PASTKI qirrasi.
 */
export const PageHeader = ({
  title,
  description,
  back,
  filters,
  filtersLabel,
  actions,
  className,
}: PageHeaderProps) => {
  const wide = useMediaQuery(WIDE);

  return (
    <header
      className={cn(
        'border-border flex flex-wrap items-end justify-between gap-3 border-b pb-4',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        {back}
        <h1 className="text-text text-xl font-semibold tracking-tight">{title}</h1>
        {description === undefined ? null : (
          <p className="text-text-muted max-w-prose text-sm">{description}</p>
        )}
      </div>

      {filters === undefined && actions === undefined ? null : (
        <div className="flex w-full flex-wrap items-end gap-3 md:w-auto">
          {filters === undefined ? null : wide ? (
            <div
              role="group"
              aria-label={filtersLabel}
              className="flex flex-wrap items-end gap-3"
            >
              {filters}
            </div>
          ) : (
            <FilterPopover label={filtersLabel}>{filters}</FilterPopover>
          )}
          {actions}
        </div>
      )}
    </header>
  );
};

/**
 * Tor ekranda filtrlar shu panelga yig'iladi.
 *
 * Nega yig'iladi: uchta tanlagich telefon ekranida uch qatorni egallaydi va
 * ro'yxatning o'zi birinchi ekranda umuman ko'rinmay qoladi. Filtr esa
 * ro'yxatga QARAB tanlanadi — ko'rinmayotgan ro'yxatni filtrlash mumkin emas.
 */
const FilterPopover = ({ label, children }: { label?: string; children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      // Tanlagichning ro'yxati PORTALDA chiziladi — u panelning ichida
      // emas, lekin bosilganda panel yopilmasligi kerak.
      if (wrapRef.current?.contains(target) === true) return;
      if (document.querySelector('[role="listbox"]')?.contains(target) === true) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <Button
        variant="secondary"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="w-full"
      >
        <SlidersHorizontal size={16} aria-hidden />
        {t('page.filters')}
      </Button>

      {open ? (
        <div
          role="group"
          aria-label={label}
          className={cn(
            'motion-listbox-in absolute top-full right-0 left-0 z-[var(--z-dropdown)] mt-2',
            'border-border bg-surface flex flex-col gap-3 rounded-[var(--radius-md)] border p-3',
            'shadow-[var(--shadow-2)]',
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};
